/* eslint-disable max-lines -- one route page wiring four queries (list, metrics, brands,
   warehouses) plus the row-actions refetch callback; splitting it up would scatter one page's
   state across files for no readability gain. */
import { useQuery } from '@apollo/client/react';
import {
  buttonVariants,
  DimeAlert,
  DimeAlertDescription,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
  toast,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Layers3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { makeBomColumns } from '../../modules/bom/bom-columns.js';
import { buildBomListWhere, type BomStatusFilter } from '../../modules/bom/bom-list-where.js';
import {
  GET_BOM_METRICS,
  GET_BOMS,
  type BomListItem,
  type BomWhere,
  type GetBomMetricsQueryResult,
  type GetBomMetricsQueryVariables,
  type GetBomsQueryResult,
  type GetBomsQueryVariables,
} from '../../modules/bom/operations.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_LOCATION_GROUPS_LIST,
  type GetLocationGroupsListQueryResult,
} from '../../modules/location-groups/operations.js';
import {
  GET_BRAND_OPTIONS,
  type GetBrandOptionsQueryResult,
} from '../../modules/products/operations.js';
import { BomListFilters } from './-BomListFilters.js';
import { BomMetricsTiles } from './-BomMetricsTiles.js';

// Column id -> Prisma orderBy path, verified against SORTABLE_FIELDS.Bom/SORTABLE_RELATIONS.Bom.
export const BOM_SORT_MAP: Record<string, string[]> = {
  num: ['num'],
  description: ['description'],
  brand: ['brand', 'name'],
  activeFlag: ['activeFlag'],
  dateLastModified: ['dateLastModified'],
};

export const Route = createFileRoute('/boms/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: BomListPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BomListPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('bom:edit');

  const [statusFilter, setStatusFilter] = useState<BomStatusFilter>('active');
  const [brandId, setBrandId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');

  const { data: brandsData, error: brandsError } = useQuery<GetBrandOptionsQueryResult>(
    GET_BRAND_OPTIONS,
    { variables: { first: 100 } },
  );
  const brands = brandsData?.brands.edges.map((edge) => edge.node) ?? [];

  const { data: warehousesData, error: warehousesError } =
    useQuery<GetLocationGroupsListQueryResult>(GET_LOCATION_GROUPS_LIST, {
      variables: { first: 100 },
    });
  const warehouses = warehousesData?.locationGroups.edges.map((edge) => edge.node) ?? [];

  const where = buildBomListWhere(statusFilter, brandId, warehouseId);

  const {
    rows: boms,
    loading,
    error: bomsError,
    refetch: refetchBoms,
    tableProps,
    search,
  } = useListTable<GetBomsQueryResult, GetBomsQueryVariables, BomListItem, BomWhere>({
    query: GET_BOMS,
    extractConnection: (data) => data?.boms,
    defaultSort: [{ id: 'num', desc: false }],
    sortMap: BOM_SORT_MAP,
    where,
    // Keeps rows whose brand/part field errors for lack of permission instead of discarding all.
    errorPolicy: 'all',
    sortTiebreakerField: 'id', // BomOrderBy lists `id` as sortable (widened for this tiebreaker pre-migration).
  });
  const rowOffset = tableProps.pageIndex * tableProps.pageSize;

  const {
    data: metricsData,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<GetBomMetricsQueryResult, GetBomMetricsQueryVariables>(GET_BOM_METRICS, {
    variables: {
      brandId: brandId || undefined,
      locationGroupId: warehouseId || undefined,
      search: search || undefined,
    },
    errorPolicy: 'all',
  });

  const columns = useMemo(
    () =>
      makeBomColumns(navigate, canEdit, {
        rowOffset,
        warehouseId: warehouseId || undefined,
        onChanged: () => {
          // onFirstPage() hasn't flushed yet, so pass `after` or refetch reuses the stale cursor.
          tableProps.onFirstPage();
          void Promise.all([refetchBoms({ after: undefined }), refetchMetrics()])
            .then((results) => {
              if (results.some((result) => result.error)) {
                toast.error('BOM list may be out of date — refresh the page to see the latest.');
              }
            })
            .catch(() => {
              toast.error('BOM list may be out of date — refresh the page to see the latest.');
            });
        },
      }),
    [navigate, canEdit, rowOffset, warehouseId, refetchBoms, refetchMetrics, tableProps],
  );

  const metrics = metricsData?.bomMetrics ?? { totalCount: 0, activeCount: 0, inactiveCount: 0 };
  // WHY: a benign per-row field error also sets bomsError but still renders rows fine, so only banner when it left nothing to show.
  const showBomsError = Boolean(bomsError) && boms.length === 0;
  const metricsSettled = Boolean(metricsData) || Boolean(metricsError);

  const newBomAction = (
    <PermissionGuard
      required={['bom:edit', 'locationGroup:view']}
      pass={
        <Link to="/boms/new" className={buttonVariants({})}>
          + New BOM
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New BOM
        </span>
      }
    />
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">
          Bill of Materials
        </DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Manage BOMs — output parts, ingredients, and build instructions.
        </DimeCardDescription>
        <DimeCardAction>{newBomAction}</DimeCardAction>
      </DimeCardHeader>
      {showBomsError && (
        <DimeAlert variant="destructive" className="mb-4">
          <DimeAlertDescription>BOMs unavailable — refresh to try again.</DimeAlertDescription>
        </DimeAlert>
      )}
      {metricsError ? (
        <DimeAlert variant="destructive" className="mb-4">
          <DimeAlertDescription>Metrics unavailable — refresh to try again.</DimeAlertDescription>
        </DimeAlert>
      ) : (
        <BomMetricsTiles metrics={metrics} loading={!metricsSettled} />
      )}
      {!loading &&
      boms.length === 0 &&
      !search &&
      !brandId &&
      !warehouseId &&
      statusFilter === 'active' &&
      !metricsError &&
      !showBomsError &&
      metricsData?.bomMetrics?.totalCount === 0 ? (
        <ListEmptyState
          icon={<Layers3 />}
          title="No BOMs yet"
          description="Bills of materials define the output part, ingredients, and build instructions for manufacturing. Add your first BOM to get started."
          action={newBomAction}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={boms}
          maxBodyHeight="viewport"
          searchColumn="num"
          searchPlaceholder="Search by BOM name or output part…"
          filters={
            <BomListFilters
              brands={brands}
              warehouses={warehouses}
              brandsUnavailable={Boolean(brandsError)}
              warehousesUnavailable={Boolean(warehousesError)}
              status={statusFilter}
              brandId={brandId}
              warehouseId={warehouseId}
              onStatusChange={setStatusFilter}
              onBrandChange={setBrandId}
              onWarehouseChange={setWarehouseId}
            />
          }
          showViewOptions
          emptyMessage={
            showBomsError
              ? 'Unable to load BOMs.'
              : 'No BOMs found. Try adjusting your search or filters.'
          }
          onRowClick={(bom) => {
            void navigate({
              to: '/boms/$bomId',
              params: { bomId: bom.id },
              search: { edit: false },
            });
          }}
          loadingRowCount={tableProps.pageSize}
          exportFileName="boms"
          {...tableProps}
          // WHY: stays loading here so an empty result doesn't flash this table's default empty message before ListEmptyState can take over once metrics settles.
          isLoading={loading || (boms.length === 0 && !metricsSettled)}
        />
      )}
    </DimeCard>
  );
}
